import { STANDARD_AIB_GC_COUNTRIES } from "@/constants";
import { intersection, sortBy, uniq, without } from "lodash-es";

import type { ForwardInventoryMandate } from "../solderaInventoryTypes";

/**
 * The staff picker's product warnings (`deal_picker_service._product_warnings`) on the buyer's own
 * selection. Worked out here from the countries and technologies the mandate rows already show, so they
 * reveal nothing the page does not and cost no listing request. The scheme warning, and Poland mixed
 * with Belgium, are left out: an offer must be of a single scheme (Poland's is RGP), which the offer
 * form already enforces.
 */
export type SelectionWarning =
  | { kind: "pl_be_mixed"; flagged: string[]; others: string[] }
  | { kind: "standard_aib_gc_mixed"; standard: string[]; outside: string[] }
  | { kind: "technology_mixed"; advanced: string[]; hsw: string[] };

// `deal_picker_service.PL_BE_COUNTRIES`, `ADVANCED_ENERGY_TYPES` and `constants.HSW_ENERGY_TYPES`.
const PL_BE_COUNTRIES = ["PL", "BE"];
const ADVANCED_ENERGY_TYPES = ["biomass", "nuclear"];
const HSW_ENERGY_TYPES = ["hydro", "solar", "wind"];

/**
 * Advice on the taken mandates as one product; never blocks an offer.
 *
 * @returns one warning per rule the selection breaks, in a fixed order.
 */
export function selectionWarnings(
  mandates: readonly Pick<
    ForwardInventoryMandate,
    "countries" | "technologies"
  >[]
): SelectionWarning[] {
  const countries = sortBy(
    uniq(mandates.flatMap((mandate) => mandate.countries))
  );
  const technologies = sortBy(
    uniq(mandates.flatMap((mandate) => mandate.technologies))
  );

  const warnings: SelectionWarning[] = [];

  const flagged = intersection(countries, PL_BE_COUNTRIES);
  const others = without(countries, ...PL_BE_COUNTRIES);
  if (flagged.length > 0 && others.length > 0) {
    warnings.push({ kind: "pl_be_mixed", flagged, others });
  }

  const standard = intersection(countries, STANDARD_AIB_GC_COUNTRIES);
  const outside = without(countries, ...STANDARD_AIB_GC_COUNTRIES);
  if (standard.length > 0 && outside.length > 0) {
    warnings.push({ kind: "standard_aib_gc_mixed", standard, outside });
  }

  const advanced = intersection(technologies, ADVANCED_ENERGY_TYPES);
  const hsw = intersection(technologies, HSW_ENERGY_TYPES);
  if (advanced.length > 0 && hsw.length > 0) {
    warnings.push({ kind: "technology_mixed", advanced, hsw });
  }

  return warnings;
}
